/**
 * AppICEMAP — Reports API
 * Fetch, create, and query reports from Directus.
 */

import { filterActive } from "../utils/expiry.js";
import { filterInUS } from "../utils/geo.js";
import {
	getClient,
	readItems,
	readItem,
	createItem,
	deleteItem,
	withToken,
} from "./directus.js";

const REPORTS_COLLECTION = "reports";

/** Fields to fetch for report list (viewport) */
const REPORT_LIST_FIELDS = [
	"id",
	"title",
	"description",
	"category.id",
	"category.slug",
	"category.label",
	"category.icon",
	"category.color_ramp",
	"category.marker_shape",
	"category.ttl_minutes",
	"location",
	"location_precision",
	"address",
	"user_created.id",
	"user_created.first_name",
	"user_created.last_name",
	"user_created.karma",
	"user_created.is_limited",
	"display_name_mode",
	"status",
	"confirmation_count",
	"denial_count",
	"created_at",
	"updated_at",
	"expires_at",
	"expiring_at",
	// Alias O2M hacia report_photos (creado en Directus): permite traer las
	// fotos en la misma consulta en vez de una petición por reporte.
	"photos.id",
	"photos.image",
	"photos.sort",
];

/**
 * Fetch reports within map bounds.
 * @param {{ sw: [number, number], ne: [number, number] }} bounds - SW and NE lat/lng
 * @param {{ categories?: string[], limit?: number }} [options]
 * @returns {Promise<Array>}
 */
export async function fetchReportsInBounds(bounds, options = {}) {
	const client = getClient();
	const { categories, limit = 200 } = options;

	// Build filter: active/expiring status + within bounding box.
	// Directus `_intersects_bbox` needs a valid GeoJSON geometry — a rectangle
	// Polygon of the viewport. (A "Point" with 4 numbers is invalid GeoJSON and
	// silently matches nothing, which is why reports never appeared on the map.)
	// bounds.sw / bounds.ne are [lat, lng]; GeoJSON coordinates are [lng, lat].
	const [swLat, swLng] = bounds.sw;
	const [neLat, neLng] = bounds.ne;
	const filter = {
		_and: [
			{ status: { _in: ["active", "expiring"] } },
			{
				location: {
					_intersects_bbox: {
						type: "Polygon",
						coordinates: [
							[
								[swLng, swLat],
								[neLng, swLat],
								[neLng, neLat],
								[swLng, neLat],
								[swLng, swLat],
							],
						],
					},
				},
			},
		],
	};

	// Add category filter if provided
	if (categories && categories.length > 0) {
		filter._and.push({
			category: {
				slug: { _in: categories },
			},
		});
	}

	try {
		const result = await client.request(
			readItems(REPORTS_COLLECTION, {
				fields: REPORT_LIST_FIELDS,
				filter,
				sort: "-created_at",
				limit,
			}),
		);
		// The server filter can only go by `status`, which nothing updates, so
		// TTL is applied here (see utils/expiry.js). Anything outside the
		// coverage area is dropped too — the map cannot even show it.
		return filterInUS(filterActive(Array.isArray(result) ? result : []));
	} catch (err) {
		console.error("[API] fetchReportsInBounds error:", err);
		throw err;
	}
}

/**
 * Fetch a single report by ID with all relations.
 * @param {string} id
 * @returns {Promise<object>}
 */
export async function fetchReportById(id) {
	const client = getClient();

	try {
		const result = await client.request(
			// `photos` is NOT a field on reports: the images live in the
			// report_photos collection and are fetched with fetchReportPhotos().
			readItem(REPORTS_COLLECTION, id, { fields: REPORT_LIST_FIELDS }),
		);
		return result;
	} catch (err) {
		console.error("[API] fetchReportById error:", err);
		throw err;
	}
}

/**
 * Create a new report.
 * @param {{
 *   title: string,
 *   description?: string,
 *   category: string,
 *   location: { type: 'Point', coordinates: [number, number] },
 *   location_precision: 'exact' | 'approx',
 *   display_name_mode: 'real' | 'anonymous',
 *   photos?: string[]
 * }} payload
 * @returns {Promise<object>}
 */
export async function createReport(payload) {
	const client = getClient();

	try {
		const result = await client.request(
			createItem(REPORTS_COLLECTION, payload),
		);
		return result;
	} catch (err) {
		console.error("[API] createReport error:", err);
		throw err;
	}
}

/**
 * Fetch reports created by the current user.
 * @param {{ limit?: number, status?: string }} [options]
 * @returns {Promise<Array>}
 */
export async function fetchMyReports(options = {}) {
	const client = getClient();
	const { limit = 100, status } = options;

	const filter = {};
	if (status) {
		filter.status = { _eq: status };
	}

	try {
		const result = await client.request(
			readItems(REPORTS_COLLECTION, {
				fields: [
					"id",
					"title",
					"category.slug",
					"category.label",
					"category.color_ramp",
					"status",
					"confirmation_count",
					"denial_count",
					"created_at",
				],
				filter: {
					...filter,
					user_created: {
						_eq: "$CURRENT_USER",
					},
				},
				sort: "-created_at",
				limit,
			}),
		);
		return result || [];
	} catch (err) {
		console.error("[API] fetchMyReports error:", err);
		return [];
	}
}

/**
 * Delete a report. Restricted to administrators by Directus permissions — the
 * UI hides the action for everyone else, but the server is what enforces it.
 *
 * @param {string} id - Report UUID
 * @returns {Promise<void>}
 */
export async function deleteReport(id) {
	const client = getClient();
	await client.request(deleteItem(REPORTS_COLLECTION, id));
}

/* ── Photos ──────────────────────────────────────────────────────────────── */

/** Collection linking a report to its uploaded images */
const REPORT_PHOTOS_COLLECTION = "report_photos";

/**
 * Attach uploaded files to a report.
 *
 * There is no `photos` field on `reports` — the relation lives in its own
 * collection with no alias back — so sending `photos: [...]` in the create
 * payload did nothing at all: the file reached Directus and the link was never
 * made. Each image needs its own row here.
 *
 * @param {string} reportId
 * @param {string[]} fileIds - UUIDs returned by uploadPhoto()
 * @returns {Promise<void>}
 */
export async function linkReportPhotos(reportId, fileIds) {
	if (!reportId || !Array.isArray(fileIds) || fileIds.length === 0) return;

	const client = getClient();

	await Promise.all(
		fileIds.map((fileId, index) =>
			client.request(
				createItem(REPORT_PHOTOS_COLLECTION, {
					report: reportId,
					image: fileId,
					sort: index,
				}),
			),
		),
	);
}

/**
 * Photos attached to a report, ordered.
 * Never throws — an image problem must not take the detail sheet down.
 *
 * @param {string} reportId
 * @returns {Promise<Array<{ id: string, image: string }>>}
 */
export async function fetchReportPhotos(reportId) {
	if (!reportId) return [];

	const client = getClient();

	try {
		const result = await client.request(
			readItems(REPORT_PHOTOS_COLLECTION, {
				fields: ["id", "image", "sort"],
				filter: { report: { _eq: reportId } },
				sort: ["sort"],
				limit: 10,
			}),
		);
		return Array.isArray(result) ? result : [];
	} catch (err) {
		console.warn("[API] fetchReportPhotos error:", err.message);
		return [];
	}
}
